import { useState, useEffect, useCallback } from 'react';
import Kedo from '@/Kedo';
import { Block, DateFormat, ChartDataItem, LineChartDataItem, BarChartDataItem } from '../types';
import {
    transformToPieData,
    transformToLineData,
    transformToBarData,
    transformLinkDataToBar,
    transformLinkDataToLine,
    transformLinkDataToPie,
    transformLinkDateBarData,
    getTransformedKeys,
    hasFieldColors,
    getUniqueFromData,
} from '../utils/chartDataTransformers';

interface ListField {
    def_field: {
        def_field_lists: Array<{
            id: number;
            color?: string;
        }>;
    };
}

interface UseChartDataOptions {
    kedo: Kedo;
    block: Block;
    dossier?: { id: number };
    link?: number;
    enabled?: boolean;
}

interface TransformedChartData {
    pieData: ChartDataItem[];
    lineData: LineChartDataItem[];
    barData: BarChartDataItem[];
    tableData: unknown[];
    keys: string[];
}

interface UseChartDataResult {
    rawData: unknown[] | null;
    transformedData: TransformedChartData | null;
    listField: ListField | null;
    loading: boolean;
    error: Error | null;
    dateFormat: DateFormat;
    setDateFormat: (format: DateFormat) => void;
    hasColors: boolean;
    refetch: () => Promise<void>;
}

export const useChartData = ({
    kedo,
    block,
    dossier,
    link,
    enabled = true,
}: UseChartDataOptions): UseChartDataResult => {
    const [rawData, setRawData] = useState<unknown[] | null>(null);
    const [transformedData, setTransformedData] = useState<TransformedChartData | null>(null);
    const [listField, setListField] = useState<ListField | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [dateFormat, setDateFormat] = useState<DateFormat>(
        (block.settings?.date_format as DateFormat) ?? 'year_month'
    );

    const fetchData = useCallback(async () => {
        const environment = kedo.env().getEnvironment();
        if (!environment || !enabled) return;

        const fieldId = block.settings?.count_list_field;
        if (!block.id || !fieldId) return;

        const api = kedo.api();
        const params: Record<string, unknown> = {};

        if (dateFormat) {
            params.date_format = dateFormat;
        }

        if (dossier?.id) {
            params.dossier = dossier.id;
            params.linkId = link;
        }

        setLoading(true);
        setError(null);

        try {
            const [dataResponse, fieldResponse] = await Promise.all([
                api.get(`${api.getMyPageBlockEndpoint(block.id)}/data`, { params }),
                api.getCached(api.getDefDossierDefFieldEndpoint(fieldId)),
            ]);

            const data = dataResponse.data;
            const field = fieldResponse.data as ListField;

            setRawData(data);
            setListField(field);

            const transformed = transformChartDataByFormat(data, field, block, kedo);
            setTransformedData(transformed);
        } catch (err) {
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    }, [kedo, block, dossier, link, dateFormat, enabled]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const hasColors = hasFieldColors(listField);

    return {
        rawData,
        transformedData,
        listField,
        loading,
        error,
        dateFormat,
        setDateFormat,
        hasColors,
        refetch: fetchData,
    };
};

const transformChartDataByFormat = (
    data: unknown[],
    listField: ListField,
    block: Block,
    kedo: Kedo
): TransformedChartData => {
    const chartFormat = block.settings?.chart_format;
    const isDateOriented = block.settings?.use_creation_date || block.settings?.date_field;

    const result: TransformedChartData = {
        pieData: [],
        lineData: [],
        barData: [],
        tableData: [],
        keys: getTransformedKeys(listField, kedo),
    };

    const typedData = data as Array<{
        '1': string | number;
        '2': string | number | null;
        yearAnddate?: string | null;
        summary?: string | null;
    }>;

    if (isDateOriented) {
        if (chartFormat === 'bar') {
            result.barData = transformToBarData(typedData, listField, kedo);
        } else if (chartFormat === 'line' || !chartFormat) {
            result.lineData = transformToLineData(typedData, listField, kedo);
        } else if (chartFormat === 'pie') {
            const pieFromLine = transformToLineData(typedData, listField, kedo);
            result.pieData = pieFromLine.map((item) => ({
                id: item.id,
                label: item.id,
                value: item.data.length > 0 ? item.data[0].y : 0,
                color: item.color,
            }));
        }
        result.tableData = transformToLineData(typedData, listField, kedo);
    } else {
        result.pieData = transformToPieData(typedData, listField, kedo);
        result.tableData = result.pieData;
    }

    return result;
};

interface UseLinkChartDataOptions {
    kedo: Kedo;
    block: Block;
    dossier?: { id: number };
    link?: number;
    enabled?: boolean;
}

interface UseLinkChartDataResult {
    rawData: unknown[] | null;
    transformedData: TransformedChartData | null;
    originField: ListField | null;
    loading: boolean;
    error: Error | null;
    dateFormat: DateFormat;
    setDateFormat: (format: DateFormat) => void;
    refetch: () => Promise<void>;
}

export const useLinkChartData = ({
    kedo,
    block,
    dossier,
    link,
    enabled = true,
}: UseLinkChartDataOptions): UseLinkChartDataResult => {
    const [rawData, setRawData] = useState<unknown[] | null>(null);
    const [transformedData, setTransformedData] = useState<TransformedChartData | null>(null);
    const [originField, setOriginField] = useState<ListField | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [dateFormat, setDateFormat] = useState<DateFormat>(
        (block.settings?.date_format as DateFormat) ?? 'year_month'
    );

    const fetchData = useCallback(async () => {
        const environment = kedo.env().getEnvironment();
        if (!environment || !enabled) return;

        if (!block.id || !block.settings?.count_link_field) return;

        const api = kedo.api();
        const params: Record<string, unknown> = {};

        if (dossier?.id) {
            params.dossier = dossier.id;
            params.linkId = link;
        }

        if (dateFormat) {
            params.date_format = dateFormat;
        }

        setLoading(true);
        setError(null);

        try {
            const dataResponse = await api.get(`${api.getMyPageBlockEndpoint(block.id)}/data`, {
                params,
            });

            const data = dataResponse.data;
            setRawData(data);

            const transformed = transformLinkChartData(data, block, kedo);
            setTransformedData(transformed);

            if (block.settings?.origin_display_field) {
                const originResponse = await api.getCached(
                    api.getDefDossierDefFieldEndpoint(block.settings.origin_display_field)
                );
                setOriginField(originResponse.data as ListField);
            }
        } catch (err) {
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    }, [kedo, block, dossier, link, dateFormat, enabled]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        rawData,
        transformedData,
        originField,
        loading,
        error,
        dateFormat,
        setDateFormat,
        refetch: fetchData,
    };
};

const transformLinkChartData = (
    data: unknown[],
    block: Block,
    kedo: Kedo
): TransformedChartData => {
    const chartFormat = block.settings?.chart_format;
    const isDateOriented = block.settings?.use_creation_date || block.settings?.date_field;

    const typedData = data as Array<{
        '1': string | number;
        '2': string | number | null;
        yearAnddate?: string | null;
        summary?: string | null;
    }>;

    const result: TransformedChartData = {
        pieData: [],
        lineData: [],
        barData: [],
        tableData: data,
        keys: [],
    };

    if (isDateOriented) {
        if (chartFormat === 'bar') {
            result.barData = transformLinkDateBarData(typedData, 'yearAnddate', 'summary', '1');
            result.keys = getUniqueFromData(typedData, 'summary');
        } else if (chartFormat === 'line') {
            result.lineData = transformLinkDataToLine(typedData, kedo);
        }
    } else {
        if (chartFormat === 'bar') {
            result.barData = transformLinkDataToBar(typedData, kedo);
        } else if (chartFormat === 'line') {
            result.lineData = transformLinkDataToLine(typedData, kedo);
        } else {
            result.pieData = transformLinkDataToPie(typedData, kedo);
        }
    }

    return result;
};
